import {
  createContext,
  use,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentProps,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';

import {
  Badge,
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Flex,
  Grid,
  Icon,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  ScrollArea,
  Spinner,
  Text,
  cn,
  type IconType,
} from '@kognitos/lattice';
import { captureException } from '@/modules/sentry/helpers/capture';
import { CatalogLevelTile } from '@/modules/process-catalog/components/CatalogLevelTile';
import {
  catalogWorkstreamAccentClasses,
  type ICatalogAccentClasses,
} from '@/modules/process-catalog/workstream-accent';
import {
  AssetTemplateCard,
  AssetTemplateGroupSelect,
  AssetTemplateRail,
} from './AssetTemplateGallery';

const SEARCH_PAGE_SIZE = 60;

/**
 * A badge on a template row, e.g. readiness or step count. Gallery cards show
 * it as quiet muted text instead of a pill.
 */
export interface IAssetTemplateBadge {
  label: string;
  /** Semantic token classes, e.g. `border-success bg-success/10` (rows only). */
  className?: string;
}

/** One template: the thing a user picks. */
export interface IAssetTemplateItem<T> {
  id: string;
  title: string;
  description?: string;
  badges?: IAssetTemplateBadge[];
  /** Extra words search should match, e.g. keywords or detector names. */
  searchText?: string;
  /** A line under the title on a gallery card, e.g. other names it goes by. */
  subtitle?: string;
  /** Leading visual on a gallery card, e.g. an avatar. */
  media?: ReactNode;
  data: T;
}

/** A second-level group of templates inside a folder. */
export interface IAssetTemplateGroup<T> {
  id: string;
  label: string;
  /** The group's visual on a gallery banner tile, e.g. stacked avatars. */
  media?: ReactNode;
  items: IAssetTemplateItem<T>[];
}

/**
 * A top-level folder, normally a workstream. Its `id` matches the composer
 * banner's folder ids so a banner click opens it.
 */
export interface IAssetTemplateFolder<T> {
  id: string;
  label: string;
  groups: IAssetTemplateGroup<T>[];
}

/** Copy for the browser; every catalog passes its own nouns. */
export interface IAssetTemplateBrowserLabels {
  title: string;
  description?: string;
  searchPlaceholder: string;
  /** e.g. "120 activities". */
  itemCount: (count: number) => string;
  /** e.g. "8 process groups". */
  groupCount: (count: number) => string;
  noMatches: (query: string) => string;
  /** Footer line explaining the hierarchy. */
  hint?: string;
  /** Accessible name for a row, e.g. "Create a draft for …". */
  itemAction?: (title: string) => string;
  /** Gallery rail: the row for every template, e.g. "All roles". */
  allItems?: string;
  /** Gallery rail: its accessible name, e.g. "Filter roles by team". */
  railLabel?: string;
}

/**
 * How the browser lays out templates. `folders` is the two-level drill-in of
 * rows; `gallery` is a rail of groups beside a grid of cards, for catalogs
 * whose templates carry a visual identity (e.g. agent roles).
 */
export type TAssetTemplateBrowserVariant = 'folders' | 'gallery';

/** A secondary action on each gallery card, e.g. "Hire" without opening the detail. */
export interface IAssetTemplateCardAction<T> {
  label: string;
  /** Accessible name, given the template's title. */
  ariaLabel?: (title: string) => string;
  /** An icon after the label, e.g. an arrow. */
  trailingIcon?: IconType;
  onClick: (item: IAssetTemplateItem<T>) => void;
}

/** What a detail screen can do with the surrounding browser and dialog. */
export interface IAssetTemplateDetailContext {
  back: () => void;
  close: () => void;
  setBusy: (busy: boolean) => void;
  /** Switches the detail to another template by id, e.g. a related one. */
  openItem: (id: string) => void;
}

/**
 * A detail screen shown inside the dialog when a template is clicked. The
 * browser owns its header and back button; `render` supplies the body, and
 * normally ends with `AssetTemplateActions`.
 */
export interface IAssetTemplateDetail<T> {
  title?: (item: IAssetTemplateItem<T>) => string;
  /** The line under the title, e.g. a breadcrumb of where the template sits. */
  description?: (item: IAssetTemplateItem<T>) => ReactNode;
  /** A visual beside the title, e.g. a large avatar. */
  media?: (item: IAssetTemplateItem<T>) => ReactNode;
  render: (
    item: IAssetTemplateItem<T>,
    context: IAssetTemplateDetailContext,
  ) => ReactNode;
}

/**
 * Props for {@link AssetTemplateBrowser}.
 */
export interface IAssetTemplateBrowserProps<T> {
  /** The templates, or a loader for catalogs kept out of the eager bundle. */
  folders:
    | IAssetTemplateFolder<T>[]
    | (() => Promise<IAssetTemplateFolder<T>[]>);
  labels: IAssetTemplateBrowserLabels;
  /** Defaults to `folders`. */
  variant?: TAssetTemplateBrowserVariant;
  /** Folder to open and scroll to, from the banner click. */
  initialFolder?: string;
  /** Gallery: the group to filter to, from the banner click. */
  initialGroup?: string;
  /**
   * Picks a template straight away. Used when there is no `detail`. `close`
   * shuts the dialog, for picks that don't navigate away.
   */
  onSelect?: (
    item: IAssetTemplateItem<T>,
    actions: { close: () => void },
  ) => void;
  /** Opens a detail screen instead of picking straight away. */
  detail?: IAssetTemplateDetail<T>;
  /**
   * Shows a spinner on the row with this `item.id` while its pick is in flight.
   * Matched by id alone, so ids must be unique across the whole catalog.
   */
  pendingItemId?: string | null;
  /** Disables every row, e.g. while any pick is in flight. */
  isBusy?: boolean;
  /** Gallery: a secondary action on each card. Shows its spinner via `pendingItemId`. */
  cardAction?: IAssetTemplateCardAction<T>;
  /** An action offered when search matches nothing, e.g. "Build from this description". */
  noMatchesAction?: { label: string; onClick: (query: string) => void };
  /** A notice above the search, e.g. a connection problem. */
  notice?: ReactNode;
  close: () => void;
  setBusy: (busy: boolean) => void;
  'data-testid'?: string;
}

const DetailContext = createContext<IAssetTemplateDetailContext | null>(null);

/**
 * The catalog dialog shared by every asset catalog: a header, a search box,
 * and the templates, either as a two-level drill-in of folders and groups with
 * template rows, or as a gallery of cards beside a rail of groups. Clicking a
 * template picks it, or opens a detail screen in the same dialog. Rendered by
 * `AssetCatalogComposer` from its `templates` config.
 */
export function AssetTemplateBrowser<T>({
  folders: foldersSource,
  labels,
  variant = 'folders',
  initialFolder,
  initialGroup,
  onSelect,
  detail,
  pendingItemId = null,
  isBusy = false,
  cardAction,
  noMatchesAction,
  notice,
  close,
  setBusy,
  'data-testid': dataTestId = 'asset-template-browser',
}: IAssetTemplateBrowserProps<T>) {
  const { t } = useTranslation('common');
  const [lazyFolders, setLazyFolders] = useState<
    IAssetTemplateFolder<T>[] | null
  >(null);
  const loaded = Array.isArray(foldersSource) ? foldersSource : lazyFolders;
  const [query, setQuery] = useState('');
  const [visibleResults, setVisibleResults] = useState(SEARCH_PAGE_SIZE);
  const [openFolder, setOpenFolder] = useState<string | null>(
    initialFolder ?? null,
  );
  // The expanded group in `folders`, the rail's filter in `gallery`.
  const [openGroup, setOpenGroup] = useState<string | null>(
    initialGroup ?? null,
  );
  const [selected, setSelected] = useState<IAssetTemplateItem<T> | null>(null);
  const treeRef = useRef<HTMLDivElement>(null);

  const [hasLoadError, setHasLoadError] = useState(false);
  const [loadAttempt, setLoadAttempt] = useState(0);

  // A loader runs once per open (the browser mounts fresh each time) and again
  // on retry, ignoring a result that lands after unmount. The ref tracks the
  // latest loader so a retry never calls one the page has since replaced.
  const loaderRef = useRef(foldersSource);
  loaderRef.current = foldersSource;
  // eslint-disable-next-line no-restricted-syntax -- reason: load a lazily imported catalog, with cancellation and retry
  useEffect(() => {
    const source = loaderRef.current;
    if (Array.isArray(source)) return;
    let cancelled = false;
    setHasLoadError(false);
    source()
      .then(result => {
        if (!cancelled) setLazyFolders(result);
      })
      .catch(error => {
        if (cancelled) return;
        captureException(error, {
          tags: { component: 'AssetTemplateBrowser', action: 'loadFolders' },
        });
        setHasLoadError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [loadAttempt]);

  // A banner folder can sit well below the fold; wait a frame for layout.
  // eslint-disable-next-line no-restricted-syntax -- reason: imperative DOM scroll once the folders render
  useEffect(() => {
    if (!initialFolder || !loaded) return;
    const frame = requestAnimationFrame(() => {
      treeRef.current
        ?.querySelector(`[data-folder-id="${CSS.escape(initialFolder)}"]`)
        ?.scrollIntoView?.({ block: 'start' });
    });
    return () => cancelAnimationFrame(frame);
  }, [initialFolder, loaded]);

  const allItems = useMemo(
    () =>
      (loaded ?? []).flatMap(folder =>
        folder.groups.flatMap(group =>
          group.items.map(item => ({ item, folder, group })),
        ),
      ),
    [loaded],
  );

  const matches = useMemo(() => {
    const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return terms.length
      ? allItems.filter(({ item, folder, group }) => {
          const haystack = [
            item.title,
            item.subtitle,
            item.description,
            item.searchText,
            group.label,
            folder.label,
          ]
            .join(' ')
            .toLowerCase();
          return terms.every(term => haystack.includes(term));
        })
      : null;
  }, [allItems, query]);

  const isGallery = variant === 'gallery';
  const galleryItems = useMemo(() => {
    if (!isGallery) return [];
    if (matches) return matches.map(({ item }) => item);
    return allItems
      .filter(({ group }) => openGroup === null || group.id === openGroup)
      .map(({ item }) => item);
  }, [isGallery, matches, allItems, openGroup]);
  const visibleCount = isGallery
    ? galleryItems.length
    : (matches ?? allItems).length;
  const allLabel = labels.allItems ?? t('common.assetTemplates.all');
  const galleryHeading = matches
    ? t('common.assetTemplates.searchResults')
    : getGroupLabel(loaded ?? [], openGroup, allLabel);

  const [isDetailBusy, setIsDetailBusy] = useState(false);
  const detailContext: IAssetTemplateDetailContext = {
    back: () => setSelected(null),
    close,
    setBusy: busy => {
      setIsDetailBusy(busy);
      setBusy(busy);
    },
    openItem: id => {
      const next = allItems.find(({ item }) => item.id === id);
      if (next) setSelected(next.item);
    },
  };

  const selectGroup = (groupId: string | null) => {
    setOpenGroup(groupId);
    setQuery('');
  };

  const pick = (item: IAssetTemplateItem<T>) => {
    if (detail) setSelected(item);
    else onSelect?.(item, { close });
  };

  const renderRow = (
    item: IAssetTemplateItem<T>,
    subtitle = item.description,
    // Ids are unique across the catalog (see pendingItemId); the composite key only guards against duplicate data.
    key = item.id,
  ) => (
    <TemplateRow
      key={key}
      item={item}
      subtitle={subtitle}
      disabled={isBusy || pendingItemId === item.id}
      isPending={pendingItemId === item.id}
      opensDetail={Boolean(detail)}
      ariaLabel={labels.itemAction?.(item.title)}
      onSelect={() => pick(item)}
    />
  );

  if (selected && detail) {
    const detailDescription =
      detail.description?.(selected) ?? selected.description;
    return (
      <DetailContext value={detailContext}>
        <Flex
          direction="column"
          width="full"
          flex="1"
          className="min-h-0"
          data-testid={`${dataTestId}-detail`}
        >
          <DialogHeader className="w-full shrink-0 border-b px-6 py-5 pr-12">
            <Flex align={detail.media ? 'center' : 'start'} gap={3}>
              <Button
                variant="ghost"
                size="icon"
                className="-ml-2 shrink-0"
                aria-label={t('common.action.back')}
                // Going back mid-run would drop the action's result and error.
                disabled={isDetailBusy}
                onClick={() => setSelected(null)}
                data-testid={`${dataTestId}-back`}
              >
                <Icon type="ArrowLeft" size="sm" />
              </Button>
              {detail.media?.(selected)}
              <Flex direction="column" gap={1} className="min-w-0">
                <DialogTitle>
                  {detail.title?.(selected) ?? selected.title}
                </DialogTitle>
                {typeof detailDescription === 'string' ||
                detailDescription == null ? (
                  <DialogDescription>{detailDescription}</DialogDescription>
                ) : (
                  // A node like a breadcrumb can't sit inside the default <p>.
                  <DialogDescription asChild>
                    <Flex>{detailDescription}</Flex>
                  </DialogDescription>
                )}
              </Flex>
            </Flex>
          </DialogHeader>
          {/* Bodies own their padding, so a side rail can run edge to edge.
              Keyed so switching templates resets the body's state. */}
          <Flex
            key={selected.id}
            direction="column"
            flex="1"
            className="min-h-0"
          >
            {detail.render(selected, detailContext)}
          </Flex>
        </Flex>
      </DetailContext>
    );
  }

  return (
    <Flex
      direction="column"
      width="full"
      flex="1"
      className="min-h-0"
      data-testid={dataTestId}
    >
      <Flex
        direction="column"
        align="stretch"
        gap={3}
        width="full"
        paddingX={6}
        className={cn('shrink-0 pt-5', isGallery ? 'border-b pb-4' : 'pb-3')}
      >
        <DialogHeader className="w-full">
          <DialogTitle>{labels.title}</DialogTitle>
          <DialogDescription className={cn(!labels.description && 'sr-only')}>
            {labels.description ?? labels.title}
          </DialogDescription>
        </DialogHeader>
        {notice}
        <InputGroup className="w-full">
          <InputGroupAddon align="inline-start">
            <Icon type="Search" size="sm" />
          </InputGroupAddon>
          <InputGroupInput
            value={query}
            onChange={event => {
              setQuery(event.target.value);
              setVisibleResults(SEARCH_PAGE_SIZE);
            }}
            placeholder={labels.searchPlaceholder}
            aria-label={labels.searchPlaceholder}
            data-testid={`${dataTestId}-search`}
          />
          {loaded && (
            <InputGroupAddon align="inline-end">
              <Text
                as="span"
                level="xSmall"
                color="muted"
                className="tabular-nums"
                data-testid={`${dataTestId}-count`}
              >
                {labels.itemCount(visibleCount)}
              </Text>
            </InputGroupAddon>
          )}
        </InputGroup>
      </Flex>

      <Flex
        direction="column"
        align="stretch"
        gap={3}
        flex="1"
        className={cn('min-h-0', !isGallery && 'px-6 pb-5')}
      >
        <Flex flex="1" align="stretch" width="full" className="min-h-0">
          {isGallery && loaded && (
            <AssetTemplateRail
              folders={loaded}
              labels={labels}
              selectedGroup={matches ? undefined : openGroup}
              totalCount={allItems.length}
              onSelectGroup={selectGroup}
            />
          )}
          <ScrollArea
            className={cn(
              'h-full min-h-0 w-full flex-1 [&_[data-slot=scroll-area-scrollbar]]:hidden',
              isGallery && 'px-6 py-5',
            )}
          >
            {hasLoadError ? (
              <Flex
                direction="column"
                align="center"
                justify="center"
                gap={3}
                padding={6}
                width="full"
                className="rounded-xl border border-dashed"
                data-testid={`${dataTestId}-load-error`}
              >
                <Text color="muted" align="center" level="small">
                  {t('common.error.title')}
                </Text>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setLoadAttempt(attempt => attempt + 1)}
                >
                  {t('common.action.retry')}
                </Button>
              </Flex>
            ) : !loaded ? (
              <Flex justify="center" width="full" paddingY={10}>
                <Spinner size="sm" />
              </Flex>
            ) : matches?.length === 0 ? (
              <Flex
                direction="column"
                align="center"
                justify="center"
                gap={3}
                padding={6}
                width="full"
                className="rounded-xl border border-dashed"
                data-testid={`${dataTestId}-no-matches`}
              >
                <Text color="muted" align="center" level="small">
                  {labels.noMatches(query.trim())}
                </Text>
                {noMatchesAction && (
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={isBusy}
                    onClick={() => noMatchesAction.onClick(query.trim())}
                  >
                    {noMatchesAction.label}
                  </Button>
                )}
              </Flex>
            ) : isGallery ? (
              <Flex direction="column" width="full" className="@container">
                <AssetTemplateGroupSelect
                  folders={loaded}
                  labels={labels}
                  selectedGroup={matches ? undefined : openGroup}
                  totalCount={allItems.length}
                  onSelectGroup={selectGroup}
                />
                <Flex
                  align="baseline"
                  gap={2}
                  className="mb-4 hidden min-w-0 md:flex"
                  data-testid={`${dataTestId}-heading`}
                >
                  <Text weight="medium" ellipsis>
                    {galleryHeading}
                  </Text>
                  <Text level="small" color="muted" className="shrink-0">
                    {labels.itemCount(galleryItems.length)}
                  </Text>
                </Flex>
                <Grid
                  cols={1}
                  gap={3}
                  className="w-full @lg:grid-cols-2"
                  data-testid={`${dataTestId}-grid`}
                >
                  {galleryItems.map(item => (
                    <AssetTemplateCard
                      key={item.id}
                      item={item}
                      onOpen={pick}
                      action={cardAction}
                      isBusy={isBusy}
                      isPending={pendingItemId === item.id}
                    />
                  ))}
                </Grid>
              </Flex>
            ) : matches ? (
              <Flex direction="column" align="stretch" gap={3} width="full">
                <Flex
                  direction="column"
                  align="stretch"
                  width="full"
                  overflow="hidden"
                  className="divide-y divide-border rounded-xl border bg-card"
                >
                  {matches
                    .slice(0, visibleResults)
                    .map(({ item, folder, group }) =>
                      renderRow(
                        item,
                        `${folder.label} · ${group.label}`,
                        `${folder.id}/${group.id}/${item.id}`,
                      ),
                    )}
                </Flex>
                {matches.length > visibleResults && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      setVisibleResults(count => count + SEARCH_PAGE_SIZE)
                    }
                  >
                    {t('common.action.showMore')}
                  </Button>
                )}
              </Flex>
            ) : (
              <Flex
                ref={treeRef}
                direction="column"
                align="stretch"
                gap={2.5}
                width="full"
              >
                {loaded.map(folder => (
                  <FolderCard
                    key={folder.id}
                    folder={folder}
                    labels={labels}
                    isOpen={openFolder === folder.id}
                    onToggle={open => {
                      setOpenFolder(open ? folder.id : null);
                      setOpenGroup(null);
                    }}
                    openGroup={openGroup}
                    onToggleGroup={(groupId, open) =>
                      setOpenGroup(open ? groupId : null)
                    }
                    renderRow={renderRow}
                  />
                ))}
              </Flex>
            )}
          </ScrollArea>
        </Flex>

        {labels.hint && (
          <Text
            level="xSmall"
            color="muted"
            className={cn('shrink-0', isGallery && 'px-6 pb-4')}
          >
            {labels.hint}
          </Text>
        )}
      </Flex>
    </Flex>
  );
}

/**
 * Props for {@link AssetTemplateActions}.
 */
export interface IAssetTemplateActionsProps {
  label: string;
  /** Replaces `label` while `onRun` is in flight. */
  busyLabel?: string;
  icon?: IconType;
  /** An icon after the label, e.g. an arrow for "Hire →". */
  trailingIcon?: IconType;
  /** Resolve to close the dialog; throw to show the error and stay open. */
  onRun: () => Promise<void>;
  disabled?: boolean;
  /** Shown when the action fails; the error itself goes to Sentry, not the user. */
  errorFallback: string;
  /** Controls before the button, e.g. a destination picker. */
  children?: ReactNode;
  /** Muted text on the far left, e.g. a reassurance that it's editable later. */
  note?: string;
  /** An icon before the note. */
  noteIcon?: IconType;
}

/**
 * The footer of a template detail screen: optional controls, then the primary
 * action. It locks the dialog while the action runs, shows its error inline,
 * and closes the dialog when it succeeds.
 */
export function AssetTemplateActions({
  label,
  busyLabel,
  icon,
  trailingIcon,
  onRun,
  disabled = false,
  errorFallback,
  children,
  note,
  noteIcon,
}: IAssetTemplateActionsProps) {
  const context = use(DetailContext);
  const [isRunning, setIsRunning] = useState(false);
  const [error, setError] = useState<string>();
  // The context is rebuilt every render, so the unmount cleanup reads it through refs.
  const contextRef = useRef(context);
  contextRef.current = context;
  const isRunningRef = useRef(false);
  isRunningRef.current = isRunning;
  const isMountedRef = useRef(true);

  // Unmounting mid-run (e.g. the page swaps the detail) must not leave the dialog
  // locked, and a run that settles afterwards must not close whatever it now shows.
  // eslint-disable-next-line no-restricted-syntax -- reason: release the dialog lock on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (isRunningRef.current) contextRef.current?.setBusy(false);
    };
  }, []);

  const run = async () => {
    setIsRunning(true);
    context?.setBusy(true);
    setError(undefined);
    try {
      await onRun();
      if (!isMountedRef.current) return;
      context?.setBusy(false);
      context?.close();
    } catch (cause) {
      captureException(cause, {
        tags: { component: 'AssetTemplateActions', action: 'run' },
      });
      if (!isMountedRef.current) return;
      setError(errorFallback);
      context?.setBusy(false);
    } finally {
      if (isMountedRef.current) setIsRunning(false);
    }
  };

  return (
    <Flex
      direction="column"
      gap={2}
      width="full"
      paddingX={6}
      paddingY={4}
      className="shrink-0 border-t"
    >
      {error && (
        <Text level="small" color="destructive" role="alert">
          {error}
        </Text>
      )}
      <Flex align="center" justify="end" gap={3} width="full">
        {note && (
          <Flex align="center" gap={2} className="mr-auto min-w-0">
            {noteIcon && (
              <Icon
                type={noteIcon}
                size="xs"
                className="shrink-0 text-muted-foreground"
              />
            )}
            <Text level="xSmall" color="muted">
              {note}
            </Text>
          </Flex>
        )}
        {children}
        <Button
          onClick={() => void run()}
          disabled={disabled || isRunning}
          isLoading={isRunning}
          aria-busy={isRunning}
          data-testid="asset-template-primary-action"
        >
          {icon && !isRunning && <Icon type={icon} size="sm" />}
          {isRunning && busyLabel ? busyLabel : label}
          {trailingIcon && !isRunning && <Icon type={trailingIcon} size="sm" />}
        </Button>
      </Flex>
    </Flex>
  );
}

/**
 * The frame every catalog dialog uses, so all three open at the same size. It
 * is wide enough for a detail screen with a side-by-side preview.
 */
export function AssetTemplateDialogContent({
  className,
  onOpenAutoFocus,
  ...props
}: ComponentProps<typeof DialogContent>) {
  return (
    <DialogContent
      className={cn(
        'flex h-[85vh] max-h-[85vh] w-[calc(100vw-2rem)] max-w-[64rem] flex-col gap-0 overflow-hidden p-0 sm:max-w-[64rem]',
        className,
      )}
      // Focus the dialog itself, so the search isn't focused before users orient.
      onOpenAutoFocus={event => {
        onOpenAutoFocus?.(event);
        if (event.defaultPrevented) return;
        event.preventDefault();
        if (event.currentTarget instanceof HTMLElement) {
          event.currentTarget.focus();
        }
      }}
      {...props}
    />
  );
}

/**
 * The label of the group with this id, or `allLabel` when no group (or an
 * unknown one) is selected.
 *
 * @param folders - The catalog's folders
 * @param groupId - The selected group, or `null` for every template
 * @param allLabel - The label for every template, e.g. "All roles"
 * @returns The heading for the gallery grid
 */
export function getGroupLabel<T>(
  folders: IAssetTemplateFolder<T>[],
  groupId: string | null,
  allLabel: string,
): string {
  if (groupId === null) return allLabel;
  const group = folders
    .flatMap(folder => folder.groups)
    .find(candidate => candidate.id === groupId);
  return group?.label ?? allLabel;
}

//----------- SUB-COMPONENTS -------------- //

function TemplateRow<T>({
  item,
  subtitle,
  disabled,
  isPending,
  opensDetail,
  ariaLabel,
  onSelect,
}: {
  item: IAssetTemplateItem<T>;
  subtitle?: string;
  disabled: boolean;
  isPending: boolean;
  opensDetail: boolean;
  ariaLabel?: string;
  onSelect: () => void;
}) {
  return (
    <Button
      type="button"
      variant="ghost"
      disabled={disabled}
      onClick={onSelect}
      aria-label={ariaLabel}
      data-testid={`asset-template-row-${item.id}`}
      className="flex h-auto w-full items-center justify-start rounded-none px-3.5 py-2 text-left transition-colors"
    >
      {/* Spans, not Flex: Flex renders a div, which is invalid inside a button. */}
      <span className="min-w-0 flex-1">
        <Text as="span" level="small" className="block truncate">
          {item.title}
        </Text>
        {subtitle && (
          <Text
            as="span"
            level="xSmall"
            color="muted"
            className="block truncate"
          >
            {subtitle}
          </Text>
        )}
      </span>
      {item.badges?.length ? (
        <span className="flex shrink-0 items-center gap-1.5">
          {item.badges.map(badge => (
            <Badge
              key={badge.label}
              variant="outline"
              className={cn('tabular-nums', badge.className)}
            >
              {badge.label}
            </Badge>
          ))}
        </span>
      ) : null}
      {isPending && <Spinner size="sm" className="shrink-0" />}
      {opensDetail && !isPending && (
        <Icon
          type="ChevronRight"
          size="sm"
          className="shrink-0 text-muted-foreground"
        />
      )}
    </Button>
  );
}

function FolderCard<T>({
  folder,
  labels,
  isOpen,
  onToggle,
  openGroup,
  onToggleGroup,
  renderRow,
}: {
  folder: IAssetTemplateFolder<T>;
  labels: IAssetTemplateBrowserLabels;
  isOpen: boolean;
  onToggle: (open: boolean) => void;
  openGroup: string | null;
  onToggleGroup: (groupId: string, open: boolean) => void;
  renderRow: (item: IAssetTemplateItem<T>) => ReactNode;
}) {
  const { t } = useTranslation('common');
  const accent = catalogWorkstreamAccentClasses(folder.id);
  const itemCount = folder.groups.reduce(
    (count, group) => count + group.items.length,
    0,
  );

  return (
    <Collapsible
      open={isOpen}
      onOpenChange={onToggle}
      className="w-full"
      data-folder-id={folder.id}
      data-testid={`asset-template-folder-${folder.id}`}
    >
      <Flex
        direction="column"
        align="stretch"
        width="full"
        overflow="hidden"
        className={cn(
          // Same border width in both states so opening never shifts layout.
          'rounded-[15px] border bg-card transition-colors',
          isOpen ? accent.cardBorder : 'border-border',
        )}
      >
        <CollapsibleTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            className="flex h-auto w-full items-center justify-start gap-3 px-3.5 py-3 text-left transition-colors hover:bg-accent/50"
          >
            <CatalogLevelTile
              label={t('common.assetTemplates.level.l1')}
              fill={accent.tile}
              size="l1"
            />
            <span className="min-w-0 flex-1">
              <Text
                as="span"
                level="small"
                weight="medium"
                className="block truncate"
              >
                {folder.label}
              </Text>
              <Text
                as="span"
                level="xSmall"
                color="muted"
                className="block truncate"
              >
                {labels.groupCount(folder.groups.length)} ·{' '}
                {labels.itemCount(itemCount)}
              </Text>
            </span>
            <Icon
              type="ChevronRight"
              size="sm"
              className={cn(
                'shrink-0 text-muted-foreground transition-transform duration-200',
                isOpen && 'rotate-90',
              )}
            />
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <Flex
            direction="column"
            align="stretch"
            gap={1.5}
            width="full"
            padding={1}
          >
            {folder.groups.map(group => (
              <GroupCard
                key={group.id}
                group={group}
                accent={accent}
                isOpen={openGroup === group.id}
                onToggle={open => onToggleGroup(group.id, open)}
                renderRow={renderRow}
              />
            ))}
          </Flex>
        </CollapsibleContent>
      </Flex>
    </Collapsible>
  );
}

function GroupCard<T>({
  group,
  accent,
  isOpen,
  onToggle,
  renderRow,
}: {
  group: IAssetTemplateGroup<T>;
  accent: ICatalogAccentClasses;
  isOpen: boolean;
  onToggle: (open: boolean) => void;
  renderRow: (item: IAssetTemplateItem<T>) => ReactNode;
}) {
  const { t } = useTranslation('common');
  return (
    <Collapsible
      open={isOpen}
      onOpenChange={onToggle}
      className="w-full"
      data-testid={`asset-template-group-${group.id}`}
    >
      <Flex
        direction="column"
        align="stretch"
        width="full"
        overflow="hidden"
        className={cn(
          'rounded-[10px] border transition-colors',
          isOpen ? cn(accent.groupBorder, accent.wash) : 'border-border',
        )}
      >
        <CollapsibleTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            className={cn(
              // 9px = the wrapper's 10px radius minus its 1px border, so the
              // hover fill doesn't curve away from the clip at the corners.
              'flex h-auto w-full items-center justify-start gap-2.5 rounded-[9px] px-3.5 py-2 text-left transition-colors',
              accent.hoverWash,
            )}
          >
            <CatalogLevelTile
              label={t('common.assetTemplates.level.l2')}
              fill={accent.softTile}
              size="l2"
            />
            <Text
              as="span"
              level="small"
              weight="medium"
              className="min-w-0 flex-1 truncate"
            >
              {group.label}
            </Text>
            <Badge variant="secondary" className="shrink-0 tabular-nums">
              {group.items.length}
            </Badge>
            <Icon
              type="ChevronRight"
              size="sm"
              className={cn(
                'shrink-0 text-muted-foreground transition-transform duration-200',
                isOpen && 'rotate-90',
              )}
            />
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <Flex
            direction="column"
            align="stretch"
            width="full"
            paddingX={1}
            className="pb-1"
          >
            <Flex
              direction="column"
              align="stretch"
              width="full"
              overflow="hidden"
              className="divide-y divide-border rounded-md border bg-card"
            >
              {group.items.map(item => renderRow(item))}
            </Flex>
          </Flex>
        </CollapsibleContent>
      </Flex>
    </Collapsible>
  );
}
